import React from 'react';
import clsx from 'clsx';
import { ImSpinner2 } from 'react-icons/im';
import { Cell, Row, TableBody, TableHeader, SortDescriptor } from 'react-aria-components';
import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from 'react-query/types/core';

import { Button } from '@/components/v2/Button';
import { formattedDateAndTime } from '@/utils/utils';
import { Table, Column } from '@/components/v2/Table';
import OncolensToast from '@/components/OncolensToast';
import { VirtualConferenceMeeting, PaginationMeta } from '../api/virtualConferenceDashboard';
import { useResetMeetingZoomData } from '../api/resetMeetingZoomData';
import { conferenceDisplayName } from '@/utils/conferenceDisplayName';

const isNonProd = !import.meta.env.VITE_ENV?.startsWith('prod');

interface VirtualConferenceDashboardTableProps {
  data:
    | InfiniteData<{
        data: VirtualConferenceMeeting[];
        pagination: PaginationMeta;
      }>
    | undefined;
  fetchNextPage: (options?: FetchNextPageOptions | undefined) => Promise<
    InfiniteQueryObserverResult<
      {
        data: VirtualConferenceMeeting[];
        pagination: PaginationMeta;
      },
      unknown
    >
  >;
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  sortDescriptor?: SortDescriptor;
  onSortChange?: (descriptor: SortDescriptor) => any;
}

export const VirtualConferenceDashboardTable = ({
  data,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  sortDescriptor,
  onSortChange,
}: VirtualConferenceDashboardTableProps) => {
  const resetZoomData = useResetMeetingZoomData();

  const handleClearZoom = (meetingId: string) => {
    resetZoomData.mutate(meetingId, {
      onSuccess: () => OncolensToast.success('Zoom data cleared'),
      onError: () => OncolensToast.error('Failed to clear zoom data'),
    });
  };

  return (
    <>
      <Table
        sortDescriptor={sortDescriptor}
        onSortChange={onSortChange}
        className="w-full text-xs bg-white text-zinc-800"
        aria-label="Virtual Conference Dashboard"
        data-cy="virtual-conference-dashboard-table">
        <TableHeader className="text-left border-b border-zinc-200">
          <Column
            id="OrganizationName"
            className="px-2 py-4"
            isRowHeader
            allowsSorting
            data-cy="vcd-column-organization">
            Organization
          </Column>
          <Column
            id="ConferenceName"
            className="px-2 py-4"
            allowsSorting
            data-cy="vcd-column-conference">
            Conference
          </Column>
          <Column
            id="MeetingDateTime"
            className="px-2 py-4"
            allowsSorting
            data-cy="vcd-column-meeting-date-time">
            Meeting Date/Time
          </Column>
          <Column className="px-2 py-4" data-cy="vcd-column-case-count">
            # of Cases
          </Column>
          <Column className="px-2 py-4" data-cy="vcd-column-zoom-meeting-id">
            Zoom Meeting ID
          </Column>
          <Column className="px-2 py-4" data-cy="vcd-column-recording">
            Recording
          </Column>
          {isNonProd && (
            <Column className="px-2 py-4" data-cy="vcd-column-actions">
              Actions
            </Column>
          )}
        </TableHeader>
        <TableBody
          data-cy="vcd-table-body"
          renderEmptyState={() => (
            <div className="py-12 text-center text-sm text-zinc-500" data-cy="vcd-empty-state">
              No results for the selected filters.
            </div>
          )}>
          {data?.pages.map((page) =>
            page.data.map((meeting) => (
              <Row
                className="border-b even:bg-white odd:bg-zinc-100 border-zinc-200"
                key={meeting.id}>
                <StandardCell data-cy="vcd-cell-organization">
                  {meeting.organizationName}
                </StandardCell>
                <StandardCell data-cy="vcd-cell-conference">
                  {conferenceDisplayName(meeting.conferenceName, meeting.conferenceStatus)}
                </StandardCell>
                <StandardCell data-cy="vcd-cell-meeting-date-time">
                  {formattedDateAndTime(meeting.meetingDateTime)}
                </StandardCell>
                <StandardCell data-cy="vcd-cell-case-count">{meeting.caseCount}</StandardCell>
                <StandardCell data-cy="vcd-cell-zoom-meeting-id">
                  {meeting.zoomMeetingId ?? '—'}
                </StandardCell>
                <StandardCell data-cy="vcd-cell-recording">
                  <RecordingLinks recordings={meeting.recordingUrls} />
                </StandardCell>
                {isNonProd && (
                  <StandardCell data-cy="vcd-cell-actions">
                    {meeting.zoomMeetingId != null ? (
                      <button
                        type="button"
                        onClick={() => handleClearZoom(meeting.id)}
                        className="text-red-600 hover:underline"
                        data-cy="vcd-clear-zoom-button">
                        Clear Zoom
                      </button>
                    ) : (
                      '—'
                    )}
                  </StandardCell>
                )}
              </Row>
            )),
          )}
        </TableBody>
      </Table>
      {hasNextPage && (
        <div className="flex justify-center w-full py-4">
          {isFetchingNextPage ? (
            <div
              className="flex items-center gap-2 text-xs text-zinc-500"
              data-cy="vcd-loading-more">
              <ImSpinner2 className="w-4 h-4 animate-spin" />
              <span>Loading more meetings...</span>
            </div>
          ) : (
            <Button
              variant="link"
              onClickHandler={() => fetchNextPage()}
              testId="vcd-view-more-button">
              View next 50 meetings
            </Button>
          )}
        </div>
      )}
    </>
  );
};

const RecordingLinks = ({
  recordings,
}: {
  recordings: { url: string; fileType: string }[] | null;
}) => {
  if (!recordings || recordings.length === 0) return <>{'\u2014'}</>;

  if (recordings.length === 1) {
    return (
      <a
        href={recordings[0].url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
        data-cy="vcd-recording-download-link">
        Download
      </a>
    );
  }

  return (
    <span className="flex items-center gap-1">
      {recordings.map((file, idx) => (
        <React.Fragment key={file.fileType}>
          {idx > 0 && <span className="text-zinc-400">|</span>}
          <a
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
            data-cy={`vcd-recording-download-${file.fileType.toLowerCase()}`}>
            Download - {file.fileType}
          </a>
        </React.Fragment>
      ))}
    </span>
  );
};

const StandardCell = ({
  children,
  className,
  ...rest
}: {
  children: React.ReactNode;
  className?: string;
  'data-cy'?: string;
}) => (
  <Cell className={clsx(className, 'py-6 px-2')} {...rest}>
    {children}
  </Cell>
);
